/**
 * The catalogue of tools the site offers.
 *
 * Deliberately free of imports — not even from the shell. `vite.config.ts` reads
 * `TOOL_PATHS` from here to build the site-wide sitemap, and Vite's config loader
 * cannot follow an import into a linked workspace package's TypeScript source.
 * Anything locale-aware therefore lives in `toolLinks.ts`, not here.
 *
 * Names and descriptions are not here either: they are i18n keys, resolved at
 * render time. `nameKey` points into the shell's shared `tools.*` catalog, so a
 * tool is named identically here and in its own header.
 */

/**
 * `available` — deployed and usable today.
 * `development` — being built; linked, because what exists already works.
 * `planned` — nothing to link to yet.
 */
export type ToolStatus = 'available' | 'development' | 'planned'

export interface ToolEntry {
  /** Stable id, also the URL segment when the tool is deployed. */
  readonly id: string
  /** i18n key for the tool's name, from the shell's shared catalog. */
  readonly nameKey: string
  /** i18n key for the one-sentence description shown on the card. */
  readonly summaryKey: string
  /** i18n keys for the card's bullet list, in display order. */
  readonly highlightKeys: readonly string[]
  readonly status: ToolStatus
  /**
   * Site-root-relative path, or `null` while the tool has nothing to visit.
   * Each tool is built with `VITE_TOOL_PATH` set to this same value, which is what
   * keeps its canonical URLs and these links in agreement.
   */
  readonly path: string | null
}

export const TOOLS: readonly ToolEntry[] = [
  {
    id: 'subnet-calculator',
    nameKey: 'tools.subnetCalculator',
    summaryKey: 'tool.subnetCalculator.summary',
    highlightKeys: [
      'tool.subnetCalculator.highlights.visualSplit',
      'tool.subnetCalculator.highlights.cloudProfiles',
      'tool.subnetCalculator.highlights.shareLink',
    ],
    status: 'development',
    path: '/subnet-calculator/',
  },
  {
    id: 'ppi-calculator',
    nameKey: 'tools.ppiCalculator',
    summaryKey: 'tool.ppiCalculator.summary',
    highlightKeys: [
      'tool.ppiCalculator.highlights.densityAndPitch',
      'tool.ppiCalculator.highlights.physicalSize',
      'tool.ppiCalculator.highlights.presets',
    ],
    status: 'development',
    path: '/ppi-calculator/',
  },
  {
    id: 'psu-calculator',
    nameKey: 'tools.psuCalculator',
    summaryKey: 'tool.psuCalculator.summary',
    highlightKeys: [
      'tool.psuCalculator.highlights.components',
      'tool.psuCalculator.highlights.headroom',
    ],
    status: 'planned',
    path: null,
  },
]

/** The paths that actually resolve to a page, for the sitemap and for link checks. */
export const TOOL_PATHS: readonly string[] = TOOLS.flatMap((tool) =>
  tool.path === null ? [] : [tool.path],
)
